import MicroModal from 'micromodal';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import loadMetaobject from '../../core/storefrontAPI/load-metaobject';
import { closeSliderCart } from '../../sections/slider-cart';
import getProductURL from '../../core/productURL';
import variantImages from './variant-images';
import TabsAutomatic from '../tabs';
import { getRatingHTML/*, extractRatingData*/ } from '../rating';
import QuickLookZoom from './zoom';
import { nostoRecommendedProductViewEvent, nostoRecordSearchClickEvent } from '../nosto/events';
import { getHeavy, getFinalSale, getPromoMessageAndPrice, getFlag, loadProductDetailsFromStorefrontAPI, getActions, getActionsWishlist } from './product-elements';
import { getFitsOptions, getColorOptions, getSizesOptions, getSizesOptionsList, initProductOptions, getSameColorVariants, getProductPrice } from './product-options';
import { updateSocialCount } from '../wishlist';
import initWishlistListeners from '../wishlist/add-modal';

// Child (non-combined) variants have only a Size option, so their product title
// includes the color as a suffix (e.g. "Scottie Sweater - Pink stripe"). Strip it
// for display since the color is already shown via the swatches/options.
const getDisplayTitle = (product_data, variant) => {
  const has_color_option = variant.selectedOptions.some(option => option.name === 'Color');

  if (has_color_option) {
    return product_data.title;
  }

  const title_parts = product_data.title.split(' - ');

  return (title_parts.length > 1) ? title_parts.slice(0, -1).join(' - ') : product_data.title;
};

/*** Functionality used for the Quicklook, Wishlist modals, and Edit Mode in cart page ***/
export default class OpenCloseQuicklook {
  // modalType can be 'quicklook' or 'wishlist'
  constructor(modalType = 'quicklook') {
    this.modalType = modalType;
    this.quickviewErrorMessage = (modalType === 'quicklook') ? 'An error occured. The product cannot be displayed within the quick view. Please try again later.' : 'An error occured. The product cannot be displayed within the wishlist modal. Please try again later.';
    this.quickviewMobileScrollY = 0;
    this.wishlistListenersInitialized = false;

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        const activeModal = document.querySelector('.quicklook-modal.is-open');
        if (activeModal) {
          MicroModal.close(activeModal.id);
        }
      }
    });

    if (modalType === 'quicklook') {
      new QuickLookZoom();
    }
  }

  // Making sure we're only coming from a Nosto functionality (not cart edit, lookbook, Shopify product lists, etc.)
  // To do: non recommendation Nosto items
  viewAttribution = async ($item) => {
    const is_cart_edit = (typeof $item.data('cart-item-edit') !== 'undefined');
    const is_nosto_tile = ($item.closest('.nosto_element').length >= 1);

    if (is_cart_edit) {
      return;
    }

    const $tile = $item.closest('.product-tile');
    const selected_product_id = $tile.data('product-id');

    // From recommendations
    if (is_nosto_tile) {
      const recommendation_id = $item.closest('.nosto_element').attr('id');
      const result_id = $item?.closest('.nosto_element')?.dataset?.resultId || recommendation_id;
      await nostoRecommendedProductViewEvent(selected_product_id, recommendation_id, result_id, false, true);
      return;
    }

    // From search / PLPs
    let click_location = '';

    if ($tile.closest('[data-collection-grid]').length) {
      click_location = 'category';
    }

    if ($tile.closest('[data-overlay-search-results]').length) {
      click_location = 'serp';
    }

    if ($tile.closest('.ns-autocomplete-product-tile').length) {
      click_location = 'autocomplete';
    }

    if (click_location === '') {
      return;
    }

    console.log('[ASTR] Quicklook', click_location, selected_product_id, $tile.find('.product-tile__image').attr('href'));
    nostoRecordSearchClickEvent(click_location, selected_product_id, $tile.find('.product-tile__image').attr('href'));
  };

  // Re-open the search suggestions since opening the modal lost the focus
  reOpenSearchSuggestions = () => {
    if (document.body.classList.contains('quicklook-modal-search-suggestions-opened')) {
      document.body.classList.remove('quicklook-modal-search-suggestions-opened');

      const searchInput = document.querySelector('[data-overlay-search-form] .aa-Input');
      if (searchInput) {
        searchInput.focus();
        searchInput.click();
      }
    }
  };

  // Build the html based on the extracted data from the Storefront API
  populateProductDetailsDesktop = (product_data, selected_color, edit_mode = false, is_wishlist = false) => {
    const ql = document.getElementById('quicklook-modal');

    try {
      // console.log(`Populate product details`, product_data);

      if (product_data === null) {
        throw new Error('[ASTR] No product data');
      }

      if (product_data.handle.includes('gift-card')) {
        throw new Error('[ASTR] Gift cards not supported');
      }

      let color_to_use = selected_color;
      if (!color_to_use) {
        const first_available_variant = product_data.variants.edges.find(edge => edge.node.availableForSale) || product_data.variants.edges[0];
        color_to_use = first_available_variant.node.selectedOptions.find(opt => opt.name === 'Color').value;
      }

      const same_color_variants_edges = getSameColorVariants(color_to_use);

      if (same_color_variants_edges === null) {
        throw new Error('[ASTR] No variants with the selected color');
      }

      const swatches_data = (product_data.astrSwatches === null) ? [] : JSON.parse(product_data.astrSwatches.value.split('\"').join('"'));
      const same_color_variants = same_color_variants_edges.map(edge => edge.node);
      const all_variants = product_data.variants.edges.map(edge => edge.node);
      const max_images = (edit_mode || is_wishlist) ? 1 : 6;
      // const ratingData = extractRatingData(product_data?.okendoSummaryData?.value || '');

      // Data from product tiles due to API access limitations to Okendo metafield
      const ratingData = {
        reviews_average: product_data.reviews_average || '0',
        review_count: product_data.review_count || '0'
      };

      // ${(edit_mode) ? '' : getWishlistButton(product_data, same_color_variants[0])}

      const $html = `
        ${is_wishlist ? `<div class="product-information__header">
          <h2>${window.theme.strings.addToWishlist}</h2>
          <p>${window.theme.strings.selectWishlistPreferences}</p>
        </div>` : ''}

        <div class="product-information__grid" data-product-grid-content data-product-id="${product_data.id.split('/').pop()}">
          <div class="product-information__media product-images" data-product-scrollable-images>
            <div class="product-images__wrapper">
              <div class="product-images__full" data-product-full tabindex="-1">
                ${(product_data.images === null || typeof product_data.images.edges === 'undefined' || !product_data.images.edges.length) ? '' : variantImages(same_color_variants[0], max_images)}
              </div>
            </div>
          </div>

          <div class="product-information__content" data-product-scrollable-details>
            <div class="product-information__content-inner" data-product-details aria-live="polite">
                <form action="${window.theme.cart.route}" method="post" enctype="multipart/form-data">
                  <div class="product-details">
                    <hgroup>
                      ${(edit_mode || is_wishlist) ? '' : getFlag(product_data)}

                      <a class="product-details__title h2" href="${product_data.product_url}" title="${window.theme.strings.viewFullDetails}">${getDisplayTitle(product_data, same_color_variants[0])}</a>

                      ${(edit_mode || is_wishlist) ? '' : getRatingHTML(ratingData, product_data.product_url)}
                    </hgroup>

                    <div class="product-details__form-container">
                      ${getProductPrice(same_color_variants[0])}
                      ${getPromoMessageAndPrice(product_data, 'price')}
                      ${getFinalSale(product_data)}
                      ${getPromoMessageAndPrice(product_data, 'message')}
                      ${getHeavy(product_data)}
                    </div>

                    <div class="product-options">
                      ${getFitsOptions()}
                      <div data-selected-color data-selected-color-label class="product-option__label" aria-live="assertive"><span class="color-name"></span></div>
                      ${getColorOptions(all_variants, same_color_variants, swatches_data)}

                      <div class="product-option" data-quickview-option-size data-option-index="0">
                        ${getSizesOptions(same_color_variants)}
                      </div>
                    </div>

                    ${getSizesOptionsList(all_variants)}

                    <div class="product-quantity quantity">
                      <div class="quantity__fields">
                        <button type="button" class="quantity__change" aria-label="Decrement quantity" data-quickview-quantity-decrement>-</button>
                        <label for="Quantity" class="visually-hidden">Quantity</label>
                        <input type="number" class="quantity__input" aria-label="Quantity" id="Quantity" name="quantity" value="1" min="1" autocomplete="off" readonly />
                        <button type="button" class="quantity__change" aria-label="Increment quantity" data-quickview-quantity-increment>+</button>
                      </div>
                    </div>

                    <div class="product-option__sizes-extra">
                      <span class="product-option__sizes-extra-item visually-hidden">Model is <span data-quickview-extra-sizefit-height></span></span>
                      <span class="product-option__sizes-extra-item visually-hidden">wearing size <span data-quickview-extra-sizefit-size></span></span>
                      <div class="product-option__sizes-extra-item visually-hidden"><span data-quickview-extra-sizefit-sizing_recommendation></span></div>
                    </div>

                    ${!is_wishlist ? `
                      <div class="product-extra-v4 visually-hidden">
                        <div class="product-extra-v4__description visually-hidden" data-quickview-extra-description></div>
                      </div>
                    ` : ''}
                  </div>

                  ${is_wishlist ? getActionsWishlist(product_data, same_color_variants[0]) : getActions(product_data, same_color_variants[0], edit_mode)}

                  <div class="product-actions__error" data-quickview-add-to-cart-error></div>
                </form>
              </div>
            </div>
          </div>
        </div>
      `;

      const modalContent = ql.querySelector('.modal__content');
      if (modalContent) {
        modalContent.innerHTML = $html;
      }

      return null;

    } catch (error) {
      console.log('Product html error', error);
      const modalContent = ql.querySelector('.modal__content');
      if (modalContent) {
        modalContent.innerHTML = this.quickviewErrorMessage;
      }
      return null;
    }
  };

  populateProductDetailsMobile = (product_data, selected_color, edit_mode = false, is_wishlist = false) => {
    const ql = document.getElementById('quicklook-modal-mobile');

    try {
      // console.log(`Populate product details ${JSON.stringify(product_data)}`);

      if (product_data === null) {
        throw new Error('[ASTR] No product data');
      }

      if (product_data.handle.includes('gift-card')) {
        throw new Error('[ASTR] Gift cards not supported');
      }

      let color_to_use = selected_color;
      if (!color_to_use) {
        const first_available_variant = product_data.variants.edges.find(edge => edge.node.availableForSale) || product_data.variants.edges[0];
        color_to_use = first_available_variant.node.selectedOptions.find(opt => opt.name === 'Color').value;
      }

      const same_color_variants_edges = getSameColorVariants(color_to_use);

      if (same_color_variants_edges === null) {
        throw new Error('[ASTR] No variants with the selected color');
      }

      const swatches_data = (product_data.astrSwatches === null) ? [] : JSON.parse(product_data.astrSwatches.value.split('\"').join('"'));
      const same_color_variants = same_color_variants_edges.map(edge => edge.node);
      const all_variants = product_data.variants.edges.map(edge => edge.node);
      // const ratingHTML = getRatingHTML({ reviews_average: product_data.reviews_average, review_count: product_data.review_count }, product_data.product_url);

      // Data from product tiles due to API access limitations to Okendo metafield
      const ratingData = {
        reviews_average: product_data.reviews_average || '0',
        review_count: product_data.review_count || '0'
      };

      const ratingHTML = (edit_mode || is_wishlist) ? '' : getRatingHTML(ratingData, product_data.product_url);

      // console.log('swatches_data', swatches_data);
      // console.log('same_color_variants', same_color_variants);
      // console.log('all_variants', all_variants);

      const $html = `
        <div class="close-modal" role="button" data-close-quickview-mobile></div>

        <div class="quickview-mobile-content" data-quickview-mobile-content data-product-id="${product_data.id.split('/').pop()}">
          ${is_wishlist ? `<div class="quickview-mobile-content__header">
            <h2>${window.theme.strings.addToWishlist}</h2>
            <p>${window.theme.strings.selectWishlistPreferences}</p>
          </div>` : ''}

          <hgroup>
            <a class="quickview-mobile-content__title h2${ratingHTML ? ' has-rating' : ''}" href="${product_data.product_url}" title="${window.theme.strings.viewFullDetails}">${getDisplayTitle(product_data, same_color_variants[0])}</a>

            ${ratingHTML}
          </hgroup>

          <div class="quickview-mobile-content__images" data-quickview-mobile-images>
            <div class="quickview-mobile-content__image-container">
              <div class="product-images__wrapper">
                <div class="product-images__full" data-product-full tabindex="-1">
                  ${(product_data.images === null || typeof product_data.images.edges === 'undefined' || !product_data.images.edges.length) ? '' : variantImages(same_color_variants[0], 6)}
                </div>
              </div>
            </div>
          </div>

          <div class="quickview-mobile-content__details" data-quickview-mobile-details>
            <form action="${window.theme.cart.route}" method="post" enctype="multipart/form-data">
              ${getProductPrice(same_color_variants[0])}
              ${getPromoMessageAndPrice(product_data, 'price')}
              ${getFinalSale(product_data)}
              ${getPromoMessageAndPrice(product_data, 'message')}
              ${getHeavy(product_data)}

              <div class="product-options">
                ${getFitsOptions()}
                <div data-selected-color data-selected-color-label class="product-option__label" aria-live="assertive"><span class="color-name"></span></div>
                ${getColorOptions(all_variants, same_color_variants, swatches_data)}

                <div class="product-option" data-quickview-option-size data-option-index="0">
                  ${getSizesOptions(same_color_variants)}
                </div>
              </div>

              ${getSizesOptionsList(all_variants)}

              <div class="product-quantity quantity">
                <div class="quantity__fields">
                  <button type="button" class="quantity__change" aria-label="Decrement quantity" data-quickview-quantity-decrement>-</button>
                  <label for="Quantity" class="visually-hidden">Quantity</label>
                  <input type="number" class="quantity__input" aria-label="Quantity" id="Quantity" name="quantity" value="1" min="1" autocomplete="off" readonly />
                  <button type="button" class="quantity__change" aria-label="Increment quantity" data-quickview-quantity-increment>+</button>
                </div>
              </div>

              ${is_wishlist ? getActionsWishlist(product_data, same_color_variants[0]) : getActions(product_data, same_color_variants[0], edit_mode)}

              <div class="product-actions__error" data-quickview-add-to-cart-error></div>
            </form>
          </div>
        </div>
      `;

      if (ql) {
        ql.innerHTML = $html;
      }

      return null;

    } catch (error) {
      console.log('Product html error', error);
      if (ql) {
        ql.innerHTML = this.quickviewErrorMessage;
      }
      return null;
    }
  };

  openQuicklookDesktop = async (selected_product_id, selected_color = '', selected_size = '', is_cart_edit = false, selected_variant_id = '', ratingData = null, is_wishlist = false) => {
    const quicklookModal = document.getElementById('quicklook-modal');
    if (!quicklookModal) return;

    const modalContent = quicklookModal.querySelector('.modal__content');
    const is_search_suggestions = (document.querySelector('.aa-Panel') !== null);

    window.theme.quickview.isOpen = true;
    window.theme.quickview.currentType = 'desktop';
    window.theme.quickview.productId = selected_product_id;
    window.theme.quickview.isCartEdit = is_cart_edit;
    window.theme.quickview.initialVariantId = selected_variant_id;
    window.theme.quickview.isWishlist = is_wishlist;
    window.theme.quickview.ratingData = ratingData;

    // Modal sizing
    const modal_w = is_cart_edit ? 580 : 1000;
    const modal_h = is_cart_edit ? 445 : 600;
    const window_h = window.innerHeight;
    const ratio = window_h / modal_h;
    const new_modal_w = modal_w * ratio;

    if (is_cart_edit) {
      document.body.classList.add('edit-cart');
    }

    quicklookModal.classList.add('loading');
    closeSliderCart();

    if (modalContent) modalContent.innerHTML = `<img src="${modalContent.dataset.loader}" alt="Loading..." width="80" height="80" />`;

    MicroModal.show('quicklook-modal', {
      disableScroll: true,
      awaitOpenAnimation: true,
      awaitCloseAnimation: true,
      onShow: (modal) => {
        if (is_search_suggestions) document.body.classList.add('quicklook-modal-search-suggestions-opened');
        quicklookModal.removeAttribute('style');
        if ((window_h < modal_h + 20) && !is_cart_edit) {
          quicklookModal.style.minHeight = `${window_h - 40}px`;
          quicklookModal.style.maxHeight = `${window_h - 40}px`;
          quicklookModal.style.maxWidth = `${new_modal_w}px`;
        }

        modal.setAttribute('data-quickview-type', is_cart_edit ? 'edit-cart' : (is_wishlist ? 'wishlist' : 'quickview'));

        // HTML is now injected and available
        const target = modal.querySelector('.modal__content');
        const observer = new MutationObserver((mutations, obs) => {
          // Prevent some weird CLS on wishlist modal. quickview modal uses a loading animation
          window.setTimeout(() => {
            document.documentElement.classList.add('quickview-opened');

            if (is_wishlist) {
              if (!this.wishlistListenersInitialized) {
                initWishlistListeners();
                this.wishlistListenersInitialized = true;
              }
            }
          }, 100);

          if (is_wishlist) {
            updateSocialCount();
          }

          obs.disconnect();
        });
        observer.observe(target, { childList: true, subtree: true });
      },
      onClose: (modal) => {
        if (modalContent) modalContent.innerHTML = '';
        document.body.classList.remove('edit-cart');
        this.reOpenSearchSuggestions();
        if (is_wishlist) {
          document.documentElement.classList.remove('quickview-opened');
        }
        modal.removeAttribute('data-quickview-type');
        if (!window.theme.quickview.isSwitching) {
          window.theme.quickview.isOpen = false;
          window.theme.quickview.currentType = null;
        }
      },
    });

    const product_details = await loadProductDetailsFromStorefrontAPI(selected_product_id);
    if (product_details === null) {
      if (modalContent) modalContent.innerHTML = this.quickviewErrorMessage;
      return;
    }

    const metaobject_id = window.Shopify.shop.includes('staging') ? '77458997425' : '158714167472';
    product_details.returnsShipping = await loadMetaobject(metaobject_id);
    product_details.product_url = getProductURL(product_details.handle);
    window.theme.quickview.product = product_details;

    if (ratingData) {
      product_details.reviews_average = ratingData.reviews_average;
      product_details.review_count = ratingData.review_count;
    }

    this.populateProductDetailsDesktop(product_details, selected_color, is_cart_edit, is_wishlist);

    // Sync selected_color with fallback if empty
    const final_selected_color = selected_color || product_details.variants.edges.find(edge => edge.node.availableForSale)?.node.selectedOptions.find(opt => opt.name === 'Color').value || product_details.variants.edges[0].node.selectedOptions.find(opt => opt.name === 'Color').value;

    window.setTimeout(() => {
      const tablist = quicklookModal.querySelector('.product-extra-v3 [role=tablist]');
      if (tablist) new TabsAutomatic(tablist);
      initProductOptions(product_details.variants.edges, final_selected_color, selected_size);
      quicklookModal.classList.remove('loading');
    }, 100);
  };

  openQuicklookMobile = async (selected_product_id, selected_color = '', selected_size = '', is_cart_edit = false, $item = null, selected_variant_id = '', ratingData = null, is_wishlist = false) => {
    const ql = document.getElementById('quicklook-modal-mobile');
    if (!ql) {
      return;
    }

    window.theme.quickview.isOpen = true;
    window.theme.quickview.currentType = 'mobile';
    window.theme.quickview.productId = selected_product_id;
    window.theme.quickview.isCartEdit = is_cart_edit;
    window.theme.quickview.initialVariantId = selected_variant_id;
    window.theme.quickview.isWishlist = is_wishlist;
    window.theme.quickview.ratingData = ratingData;

    if (is_cart_edit) {
      document.body.classList.add('edit-cart');
    }

    closeSliderCart();
    ql.innerHTML = '';
    ql.classList.add('active');

    const fullOverlay = document.querySelector('[data-full-overlay]');
    if (fullOverlay) {
      fullOverlay.classList.add('active');
    }

    const product_details = await loadProductDetailsFromStorefrontAPI(selected_product_id);
    if (product_details === null) {
      const modalContent = ql.querySelector('.modal__content');
      if (modalContent) {
        modalContent.innerHTML = this.quickviewErrorMessage;
      } else {
        ql.innerHTML = this.quickviewErrorMessage;
      }
      return;
    }

    window.theme.quickview.product = product_details;
    product_details.product_url = getProductURL(product_details.handle);

    if (ratingData) {
      product_details.reviews_average = ratingData.reviews_average;
      product_details.review_count = ratingData.review_count;
    }

    this.populateProductDetailsMobile(product_details, selected_color, is_cart_edit, is_wishlist);

    // Sync selected_color with fallback if empty
    const final_selected_color = selected_color || product_details.variants.edges.find(edge => edge.node.availableForSale)?.node.selectedOptions.find(opt => opt.name === 'Color').value || product_details.variants.edges[0].node.selectedOptions.find(opt => opt.name === 'Color').value;

    window.setTimeout(() => {
      if (!is_cart_edit && !is_wishlist && $item) {
        const product_id = product_details.id.split('/').pop();
        const quickview_opened = new CustomEvent('quickview-mobile:loaded', { bubbles: true });
        quickview_opened.data = { detail: { productId: product_id } };
        document.dispatchEvent(quickview_opened);
        this.viewAttribution($item);
      }

      ql.setAttribute('data-quickview-type', is_wishlist ? 'wishlist' : 'quickview');

      if (is_wishlist) {
        document.documentElement.classList.add('quickview-opened');
        if (!this.wishlistListenersInitialized) {
          initWishlistListeners();
          this.wishlistListenersInitialized = true;
        }

        updateSocialCount();
      }

      initProductOptions(product_details.variants.edges, final_selected_color, selected_size);

      if (!window.theme.quickview.isSwitching) {
        this.quickviewMobileScrollY = window.scrollY || window.pageYOffset;
      }

      disableBodyScroll(ql, {
        allowTouchMove: el => {
          return !!el.closest('[data-product-full]') || !!el.closest('[data-quickview-mobile-content]');
        }
      });

      const is_search_suggestions = (document.querySelector('.aa-Panel') !== null);
      if (is_search_suggestions) {
        document.body.classList.add('quicklook-modal-search-suggestions-opened');
      }
    }, 100);
  };

  closeQuicklookDesktop = () => {
    MicroModal.close('quicklook-modal');
    window.theme.quickview.isOpen = false;
    window.theme.quickview.currentType = null;
  };

  closeQuicklookMobile = () => {
    const ql = document.getElementById('quicklook-modal-mobile');
    if (ql) {
      ql.classList.remove('active');
      enableBodyScroll(ql);
      ql.innerHTML = '';
      ql.removeAttribute('data-quickview-type');
    }

    const fullOverlay = document.querySelector('[data-full-overlay]');
    if (fullOverlay) {
      fullOverlay.classList.remove('active');
    }

    document.body.classList.remove('edit-cart');
    if (!window.theme.quickview.isSwitching) {
      window.scrollTo(0, this.quickviewMobileScrollY);
    }
    this.reOpenSearchSuggestions();

    window.theme.quickview.isOpen = false;
    window.theme.quickview.currentType = null;

    document.documentElement.classList.remove('quickview-opened');
  };
}
